import type { QuizEditionSourceMaterial } from '@/lib/domain/quiz/quizSourceMaterial';
import type {
  EditionItem,
  EditionItemErrors,
  EditionItemSavePayload,
  PollEditionItem,
  PollOption,
  QuizEditionItemRpcRow,
  ReviewItem,
  ReviewItemContent,
  TriviaEditionItem,
} from './types';
import { POLL_MAX_OPTIONS, POLL_MIN_OPTIONS } from './pollOptionConstraints';

export { POLL_MAX_OPTIONS, POLL_MIN_OPTIONS } from './pollOptionConstraints';

function normalizeItemType(value: string | null | undefined): 'trivia' | 'poll' {
  return value === 'poll' ? 'poll' : 'trivia';
}

function isHttpUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Resolve a stable source URL for a review item.
 *
 * Priority:
 * 1) Explicit source_url from item context
 * 2) source_topic when it is itself a URL
 * 3) group when it is itself a URL
 * 4) matched durable source material URL
 */
function resolveReviewItemDirectSourceUrl(item: ReviewItem): string | undefined {
  const explicit = item.context?.source_url?.trim();
  if (explicit && isHttpUrl(explicit)) return explicit;

  const sourceTopic = item.context?.source_topic?.trim();
  if (sourceTopic && isHttpUrl(sourceTopic)) return sourceTopic;

  const groupUrl = item.group?.trim();
  if (groupUrl && isHttpUrl(groupUrl)) return groupUrl;

  return undefined;
}

function normalizeSourceValue(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function resolveMatchedSourceMaterial(
  item: ReviewItem,
  sourceMaterials: QuizEditionSourceMaterial[] = []
): QuizEditionSourceMaterial | null {
  if (sourceMaterials.length === 0) {
    return null;
  }

  const sourceUrl = resolveReviewItemDirectSourceUrl(item);
  if (sourceUrl) {
    const byUrl = sourceMaterials.find(
      (material) => normalizeSourceValue(material.sourceUrl) === sourceUrl
    );
    if (byUrl) return byUrl;
  }

  const sourceTopic = normalizeSourceValue(item.context?.source_topic);
  const group = normalizeSourceValue(item.group);
  const labels = new Set([sourceTopic, group].filter((value): value is string => Boolean(value)));

  if (labels.size > 0) {
    const byLabel = sourceMaterials.find((material) => {
      const title = normalizeSourceValue(material.title);
      const sourceText = normalizeSourceValue(material.sourceText);
      return Boolean((title && labels.has(title)) || (sourceText && labels.has(sourceText)));
    });
    if (byLabel) return byLabel;
  }

  return sourceMaterials.length === 1 ? sourceMaterials[0] : null;
}

export function resolveReviewItemSourceUrl(
  item: ReviewItem,
  sourceMaterials: QuizEditionSourceMaterial[] = []
): string | undefined {
  const directSourceUrl = resolveReviewItemDirectSourceUrl(item);
  if (directSourceUrl) return directSourceUrl;

  const matchedMaterial = resolveMatchedSourceMaterial(item, sourceMaterials);
  const materialSourceUrl = normalizeSourceValue(matchedMaterial?.sourceUrl);
  return materialSourceUrl && isHttpUrl(materialSourceUrl) ? materialSourceUrl : undefined;
}

export function resolveReviewItemSourceText(
  item: ReviewItem,
  sourceMaterials: QuizEditionSourceMaterial[] = []
): string | undefined {
  const contextSourceText = normalizeSourceValue(item.context?.source_text);
  if (contextSourceText) {
    return contextSourceText;
  }

  const matchedMaterial = resolveMatchedSourceMaterial(item, sourceMaterials);
  const materialSourceText = normalizeSourceValue(matchedMaterial?.sourceText);
  if (materialSourceText) {
    return materialSourceText;
  }

  return (
    normalizeSourceValue(item.context?.source_topic) ??
    normalizeSourceValue(item.group) ??
    undefined
  );
}

export function validateReviewItem(content: ReviewItemContent): {
  valid: boolean;
  errors: ReviewItem['errors'];
} {
  const errors: ReviewItem['errors'] = {};
  const s = content.stem?.trim() || '';
  if (s.length < 10) errors.stem = 'Min length 10';
  const opts = [content.key, content.distractor_1, content.distractor_2, content.distractor_3].map(
    (s) => (s || '').trim()
  );
  if (opts.some((s) => s.length === 0)) errors.options = 'All four answers are required';
  const unique = new Set(opts);
  if (unique.size < 4) errors.options = 'Answers must be unique';
  const fact = (content.question_fact || '').trim();
  if (fact.length === 0) {
    errors.fact = 'Fun fact is required';
  } else if (fact.length > 400) {
    errors.fact = 'Fun fact is too long';
  }
  return { valid: Object.keys(errors).length === 0, errors };
}

function parsePollOptions(value: unknown): PollOption[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((entry, index) => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }

    const record = entry as Record<string, unknown>;
    const label = typeof record.label === 'string' ? record.label : '';
    const sortIndex =
      typeof record.sort_index === 'number' && Number.isFinite(record.sort_index)
        ? record.sort_index
        : index;
    const id = typeof record.id === 'string' && record.id.length > 0 ? record.id : undefined;

    return [{ id, label, sort_index: sortIndex }];
  });
}

export function inspectPollContent(input: { pollPrompt: string; pollOptions: PollOption[] }): {
  promptMissing: boolean;
  optionCount: number;
  filledOptionCount: number;
  invalidOptionCount: boolean;
  hasBlankOptionLabels: boolean;
  hasDuplicateOptionLabels: boolean;
} {
  const labels = input.pollOptions.map((option) => option.label.trim());
  const filledLabels = labels.filter(Boolean);

  return {
    promptMissing: input.pollPrompt.trim().length === 0,
    optionCount: labels.length,
    filledOptionCount: filledLabels.length,
    invalidOptionCount:
      filledLabels.length < POLL_MIN_OPTIONS || filledLabels.length > POLL_MAX_OPTIONS,
    hasBlankOptionLabels: labels.some((label) => label.length === 0),
    hasDuplicateOptionLabels: new Set(filledLabels).size !== filledLabels.length,
  };
}

function validatePollContent(input: { pollPrompt: string; pollOptions: PollOption[] }): {
  valid: boolean;
  errors: EditionItemErrors;
} {
  const errors: EditionItemErrors = {};
  const pollContent = inspectPollContent(input);

  if (pollContent.promptMissing) {
    errors.prompt = 'Poll prompt is required';
  }

  if (pollContent.invalidOptionCount) {
    errors.options = 'Poll must have between 2 and 6 options';
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

function toPollOptionsSavePayload(options: PollOption[]): Array<{
  id?: string;
  label: string;
  sort_index: number;
}> {
  return options
    .map((option) => ({
      ...option,
      label: option.label.trim(),
    }))
    .filter((option) => option.label.length > 0)
    .map((option, index) => ({
      ...(option.id ? { id: option.id } : {}),
      label: option.label,
      sort_index: index,
    }));
}

function toPollOptionsDraftPayload(options: PollOption[]): Array<{
  id?: string;
  label: string;
  sort_index: number;
}> {
  return options.map((option, index) => ({
    ...(option.id ? { id: option.id } : {}),
    label: option.label,
    sort_index:
      typeof option.sort_index === 'number' && Number.isFinite(option.sort_index)
        ? option.sort_index
        : index,
  }));
}

export function validateItem(item: EditionItem): {
  valid: boolean;
  errors: EditionItemErrors;
} {
  if (item.type === 'poll') {
    return validatePollContent({
      pollPrompt: item.pollPrompt,
      pollOptions: item.pollOptions,
    });
  }

  return validateReviewItem(item.content);
}

export function isPublishable(item: EditionItem): boolean {
  if (!item.valid) {
    return false;
  }

  if (item.type === 'poll') {
    return item.pollDefinitionId != null && item.pollDefinitionId.length > 0;
  }

  return validateReviewItem(item.content).valid;
}

export function toSavePayload(item: EditionItem): EditionItemSavePayload {
  const base = {
    ...(item.membershipId ? { id: item.membershipId } : {}),
    item_index: item.questionIndex,
    is_selected: item.includedInEdition,
    is_valid: item.valid,
  };

  if (item.type === 'poll') {
    const payload: EditionItemSavePayload = {
      ...base,
      item_type: 'poll',
      ...(item.context ? { context_json: item.context } : {}),
    };

    if (item.pollDefinitionId) {
      payload.poll_definition_id = item.pollDefinitionId;
      if (!item.pollCanEditDefinitionInline && item.valid) {
        return payload;
      }
    }

    if (!item.valid) {
      payload.poll_prompt = item.pollPrompt;
      payload.poll_options = toPollOptionsDraftPayload(item.pollOptions);
      return payload;
    }

    const prompt = item.pollPrompt.trim();
    if (prompt.length > 0) {
      payload.poll_prompt = prompt;
    }

    const pollOptions = toPollOptionsSavePayload(item.pollOptions);
    if (pollOptions.length > 0) {
      payload.poll_options = pollOptions;
    }

    return payload;
  }

  return {
    ...base,
    item_type: 'trivia',
    stem: item.content.stem,
    key: item.content.key,
    distractor_1: item.content.distractor_1,
    distractor_2: item.content.distractor_2,
    distractor_3: item.content.distractor_3,
    question_fact: item.content.question_fact || null,
    tag_names: item.content.tag_names || [],
    level2_tag_slugs: item.content.level2_tag_slugs || [],
    level3_tag_slugs: item.content.level3_tag_slugs || [],
    level4_tag_slugs: item.content.level4_tag_slugs || [],
    topic_name: item.group || item.context?.source_topic || null,
    context_json:
      item.context || item.content.level4_tag_slugs?.length
        ? {
            ...(item.context || {}),
            ...(item.content.level4_tag_slugs?.length
              ? { level4_tag_slugs: item.content.level4_tag_slugs }
              : {}),
          }
        : null,
  };
}

function fromTriviaRpcRow(row: QuizEditionItemRpcRow, index: number): TriviaEditionItem {
  const context = row.context_json ?? undefined;
  const content = {
    stem: row.stem ?? '',
    key: row.key ?? '',
    distractor_1: row.distractor_1 ?? '',
    distractor_2: row.distractor_2 ?? '',
    distractor_3: row.distractor_3 ?? '',
    question_fact: row.question_fact ?? '',
    tag_names: row.tag_names ?? undefined,
    level2_tag_slugs: row.level2_tag_slugs ?? undefined,
    level3_tag_slugs: row.level3_tag_slugs ?? undefined,
    level4_tag_slugs: row.level4_tag_slugs ?? context?.level4_tag_slugs ?? undefined,
  };
  const validation = validateReviewItem(content);

  return {
    type: 'trivia',
    membershipId: row.id ?? undefined,
    questionIndex: row.item_index ?? index,
    includedInEdition: row.is_selected ?? true,
    valid: row.is_valid ?? validation.valid,
    errors: validation.errors,
    content,
    group: row.topic_name ?? context?.source_topic ?? undefined,
    context,
  };
}

function fromPollRpcRow(row: QuizEditionItemRpcRow, index: number): PollEditionItem {
  const pollPrompt = row.poll_prompt ?? '';
  const pollOptions = parsePollOptions(row.poll_options);
  const validation = validatePollContent({ pollPrompt, pollOptions });

  return {
    type: 'poll',
    context: row.context_json ?? undefined,
    membershipId: row.id ?? undefined,
    questionIndex: row.item_index ?? index,
    includedInEdition: row.is_selected ?? true,
    valid: row.is_valid ?? validation.valid,
    errors: validation.errors,
    pollDefinitionId: row.poll_definition_id ?? undefined,
    pollPrompt,
    pollOptions,
  };
}

export function fromRpcRow(row: QuizEditionItemRpcRow, index: number): EditionItem {
  if (normalizeItemType(row.item_type) === 'poll') {
    return fromPollRpcRow(row, index);
  }

  return fromTriviaRpcRow(row, index);
}
